import { inject, Injectable, Signal } from '@angular/core';
import {
  SkuSearchResultViewModel,
  QuickOrderSelectionModel,
  ProductListPayloadModel,
  ProductModel,
  ShoppingListItemModel,
  ShoppingListModel,
  SkuSearchFoundCountItemModel,
  SkuSearchResultModel} from '@libs/shop/models';
import {
  AppError} from '@libs/shared/models';
import { firstValueFrom } from 'rxjs';
import { CatalogSearchRepositoryService } from '../catalog-search';
import { ShoppingListsState } from '../shopping-lists';
import { SkuSearchRepositoryService } from '../sku-search';

@Injectable({
  providedIn: 'root',
})
export class QuickOrderService {
  private readonly skuSearchService: SkuSearchRepositoryService = inject(SkuSearchRepositoryService);
  private readonly catalogSearchService: CatalogSearchRepositoryService = inject(CatalogSearchRepositoryService);
  private readonly shoppingListState: ShoppingListsState = inject(ShoppingListsState);

  public determineQuickOrderShoppingListId(): string {
    const shoppingLists: Signal<ShoppingListModel[]> = this.shoppingListState.entities;
    const quickOrderShoppingListId: string | undefined = shoppingLists().find(shoppingList => shoppingList.isQuickOrder)?.id;

    if (!quickOrderShoppingListId || quickOrderShoppingListId?.trim() === '') {
      throw AppError.create('determineQuickOrderShoppingListId failed. QuickOrder-ShoppingList not found', 404);
    }

    return quickOrderShoppingListId;
  }

  public async getSkuSearchResultViewModel(productIdentifiers: Record<string, number>): Promise<SkuSearchResultViewModel> {
    const skuSearchResult: SkuSearchResultModel = await firstValueFrom(this.skuSearchService.search(Object.keys(productIdentifiers)));

    const singleProductSkus: Record<string, number> = {};
    const productSkusToChoose: SkuSearchFoundCountItemModel[] = [];
    const notFoundProducts: string[] = [];

    for (const finding of skuSearchResult.found) {
      if (!finding.skus) {
        break;
      }

      if (finding.skus.length > 1) {
        productSkusToChoose.push({
          ...finding,
          count: productIdentifiers[finding.key],
        });
      } else if (finding.skus.length === 1) {
        singleProductSkus[finding.skus[0]] = productIdentifiers[finding.key];
      } else {
        notFoundProducts.push(finding.key);
      }
    }

    return {
      singleResultItems: singleProductSkus,
      multipleResultItems: productSkusToChoose,
      notFoundItems: notFoundProducts,
    };
  }

  public async getProductsToSelect(productSkusToChoose: SkuSearchFoundCountItemModel[]): Promise<QuickOrderSelectionModel[]> {
    const products: QuickOrderSelectionModel[] = [];
    const allSkus: string[] = productSkusToChoose.map(x => x.skus).flat();
    const allProducts: ProductModel[] = await firstValueFrom(this.catalogSearchService.findProductsBySkus(allSkus));

    for (const finding of productSkusToChoose) {
      if (!finding.skus) {
        break;
      }

      const product: QuickOrderSelectionModel = { key: '', items: [] };

      product.key = finding.key;

      for (const sku of finding.skus) {
        const p: ProductModel | undefined = allProducts.find(x => x.sku === sku);

        if (!p) {
          continue;
        }

        const item: ShoppingListItemModel = {
          type: 'shopping-list-items',
          id: '',
          quantity: finding.count,
          sku: sku,
          productData: p,
          calculations: undefined,
        };

        product.items.push(item);
      }

      products.push(product);
    }

    return products;
  }

  public async addToQuickOrderList(listId: string, singleProductSkus: Record<string, number>): Promise<void> {
    const collection: ProductListPayloadModel[] = Object.keys(singleProductSkus).map(sps => ({
      id: '',
      type: 'shopping-list-items',
      sku: sps,
      quantity: singleProductSkus[sps],
    }));

    await this.shoppingListState.addProductCollectionToShoppingList({
      listId: listId,
      collection,
    });
  }
}
